import React from 'react';
import logoFullSrc from '../assets/logo-full.svg';
import logoIconSrc from '../assets/logo-icon.svg';
import amdLogoIcon from '../assets/amd-logo.svg';
import amdBgLogo from '../assets/backgrounds/amd-login-background-splash.png';
import amdBackground from '../assets/backgrounds/amd-background.png';
import amdSplashImage from '../assets/amd-splash-image.png';
import { cloneDeep } from 'lodash';
import { LoginVariantProps } from '../interfaces/loginVariant';
import { defaultVariant } from '../login-variants';
import { RegistrationVariantProps } from '../interfaces/registrationVariant';
import { registrationVariants } from '../registration-variants';
import { style } from '@do/walrus';
import { isAMD } from '../utils/checkLocationHost';

export interface headerStyle {
  background?: string;
  color?: string;
}

export interface ButtonStyles {
  primary: {
    bgColor?: string;
    textColor?: string;
    hoverBgColor?: string;
    hoverTextColor?: string;
  };
  secondary?: {
    bgColor?: string;
    textColor?: string;
    hoverBgColor?: string;
    hoverTextColor?: string;
    hoverBorderColor?: string;
    borderColor?: string;
  };
  tertiary?: {
    bgColor?: string;
    textColor?: string;
    hoverBgColor?: string;
    hoverTextColor?: string;
  };
  banner?: {
    bgColor?: string;
    textColor?: string;
    hoverBgColor?: string;
    hoverTextColor?: string;
  };
}
export interface RegistrationTemplate {
  allowedAuthMethods: string[];
  topNav: {
    logo: {
      desktopLogo: string;
      mobileLogo: string;
      alt: string;
      link: string;
    };
    title: string;
    signUpTextColor: string;
    signInTextColor: string;
  };
  inputLabelColor: string;
  login: {
    poweredByDigitalOcean: boolean;
    legalInfo: boolean;
    variant?: LoginVariantProps;
  };
  registration: {
    variant?: RegistrationVariantProps;
    dataSharingConsentRequired?: boolean;
  };
  header: headerStyle;
  buttons: ButtonStyles;
  containerPaddingBottom?: boolean;
  forgotPassword: {
    fullScreenBackground: {
      url: string;
    };
  };
}

export interface RegistrationTemplateContextType {
  template: RegistrationTemplate;
}

export const amdLoginVariant: LoginVariantProps = {
  ...cloneDeep(defaultVariant),
  id: 0,
  weight: '0',
  type: 'variant',
  loginTitle: 'Log into your account',
  loginSubtitle: undefined,
  splash: {
    ...cloneDeep(defaultVariant.splash),
    background: {
      url: amdBgLogo,
    },
    titleImage: {
      size: 'xlarge',
      image: {
        url: amdSplashImage,
      },
    },
    title: {
      text: 'Experience the Power of AMD GPUs',
      color: 'light',
    },
    subtitle: {
      text: 'Built for demanding AI and high-performance computing workloads.',
      color: 'light',
    },
  },
};

const doRegistrationTemplate: RegistrationTemplate = {
  allowedAuthMethods: ['email', 'github', 'google'],
  topNav: {
    logo: {
      desktopLogo: logoFullSrc,
      mobileLogo: logoIconSrc,
      alt: 'DigitalOcean',
      link: 'https://www.digitalocean.com',
    },
    title: 'DigitalOcean',
    signUpTextColor: '#0069ff',
    signInTextColor: '#0069ff',
  },
  containerPaddingBottom: true,
  inputLabelColor: '',
  login: {
    poweredByDigitalOcean: false,
    legalInfo: false,
  },
  registration: {
    variant: undefined,
  },
  header: {},
  buttons: {
    primary: {},
  },
  forgotPassword: {
    fullScreenBackground: {
      url: '',
    },
  },
};

const amdRegistrationTemplate: RegistrationTemplate = {
  allowedAuthMethods: ['email', 'github', 'digitalocean'],
  topNav: {
    logo: {
      desktopLogo: amdLogoIcon,
      mobileLogo: amdLogoIcon,
      alt: 'AMD Developer Cloud',
      link: window.location.origin,
    },
    title: 'DigitalOcean',
    signUpTextColor: '#2899FF',
    signInTextColor: '#2899FF',
  },
  containerPaddingBottom: false,
  inputLabelColor: style.colors.black,
  login: {
    poweredByDigitalOcean: true,
    variant: amdLoginVariant,
    legalInfo: true,
  },
  registration: {
    variant: registrationVariants['amd'],
    dataSharingConsentRequired: true,
  },
  header: {
    background: style.colors.black,
    color: style.colors.white,
  },
  buttons: {
    primary: {
      bgColor: style.colors.black,
      textColor: style.colors.white,
      hoverBgColor: style.colors.shadesOfGrey[9],
      hoverTextColor: style.colors.white,
    },
    secondary: {
      bgColor: style.colors.white,
      textColor: style.colors.black,
      hoverBgColor: style.colors.black,
      hoverTextColor: style.colors.white,
      hoverBorderColor: style.colors.white,
      borderColor: style.colors.grey.dark,
    },
  },
  forgotPassword: {
    fullScreenBackground: {
      url: amdBackground,
    },
  },
};

const RegistrationTemplateContext = React.createContext({
  template: doRegistrationTemplate,
});

export const RegistrationTemplateProvider: React.FC = ({ children }) => {
  const _template = isAMD() ? amdRegistrationTemplate : doRegistrationTemplate;
  return (
    <RegistrationTemplateContext.Provider
      value={{
        template: _template,
      }}
    >
      {children}
    </RegistrationTemplateContext.Provider>
  );
};

export function useRegistrationTemplate() {
  const context = React.useContext(RegistrationTemplateContext);
  if (!context) {
    throw new Error('useRegistrationTemplate must be used within a RegistrationTemplateProvider');
  }
  return context;
}
